import { useCallback } from 'react';
import * as Y from 'yjs';
import { absoluteToFrameLocal, type Point } from "@/features/canvas/engine/primitives/geometry/geometry";
import { canvasRoots } from "@/features/canvas/engine/wiring/canvas-roots";
import { useCanvasBinding } from "@/features/canvas/engine/wiring/contexts/canvas-binding-context";

export interface AutoAttachCommit {
  nodeId: string;
  /** Where the node's origin landed in absolute (canvas-space) coordinates. */
  absoluteEndPos: Point;
  /** null when the drop is outside any frame (drag-out path — Task 8). */
  targetFrameId: string | null;
}

/**
 * Auto-attach commit on drag-stop. Wired by CanvasEngine via React Flow's
 * onNodeDragStop. All field updates run inside one Yjs transact tagged with
 * the client origin (Y.Doc.clientID — matches S2's useCanvasUndoManager
 * trackedOrigins contract) so the UndoManager treats it as a single undo step.
 *
 * Drop-onto-frame (this task): flip parentId, convert position to frame-local,
 *   maintain both frames' childOrder arrays.
 * Drop-out-of-frame (Task 8): see useAutoAttach.commit when targetFrameId is null.
 */
export function useAutoAttach(yDoc: Y.Doc, origin: number) {
  const binding = useCanvasBinding();
  const commit = useCallback((args: AutoAttachCommit) => {
    const nodes = canvasRoots(yDoc, binding).nodes;
    const node = nodes.get(args.nodeId) as Y.Map<unknown> | undefined;
    if (!node) return;

    const currentParentId = (node.get('parentId') as string | null);
    const nodeType = node.get('type') as string;

    // Frame-into-frame: frames don't auto-attach into other frames at v1.
    // Treat as a plain move — preserve the absolute end position; do not
    // touch parentId or any childOrder. (Spec: docs/1-projects/canvas/
    // session4 Task 10 case 3.)
    if (nodeType === 'frame' && args.targetFrameId !== null) {
      yDoc.transact(() => {
        node.set('position', args.absoluteEndPos);
      }, origin);
      return;
    }

    // Drop-onto path
    if (args.targetFrameId !== null) {
      const targetFrame = nodes.get(args.targetFrameId) as Y.Map<unknown> | undefined;
      if (!targetFrame) return;

      // Only frame nodes can be auto-attach targets. If a future caller passes
      // a non-frame nodeId as targetFrameId (e.g., a sticky that visually
      // overlaps the drag point), bail before touching childOrder — childOrder
      // only exists on frame data maps and accessing it on a non-frame node
      // produces a runtime crash.
      if (targetFrame.get('type') !== 'frame') return;

      // Locked frames reject drops — `locked` is the top-level CanvasNode
      // field (schema.md:506-507), not a FrameData property. One source of
      // truth, no duplicate FrameData.locked.
      if (targetFrame.get('locked') === true) return;

      const framePos = targetFrame.get('position') as Point;
      const newLocal = absoluteToFrameLocal(args.absoluteEndPos, framePos);

      yDoc.transact(() => {
        // Remove from old parent's childOrder when moving between frames.
        if (currentParentId && currentParentId !== args.targetFrameId) {
          const oldFrame = nodes.get(currentParentId) as Y.Map<unknown> | undefined;
          if (oldFrame) {
            const oldOrder = (oldFrame.get('data') as Y.Map<unknown>).get('childOrder') as Y.Array<string>;
            removeFromOrder(oldOrder, args.nodeId);
          }
        }

        node.set('parentId', args.targetFrameId);
        node.set('position', newLocal);

        // Append to target's childOrder (if not already present).
        const targetOrder = (targetFrame.get('data') as Y.Map<unknown>).get('childOrder') as Y.Array<string>;
        if (!targetOrder.toArray().includes(args.nodeId)) {
          targetOrder.push([args.nodeId]);
        }
      }, origin);
      return;
    }

    // Drop-out path: targetFrameId === null
    if (currentParentId === null) return;  // already top-level no-op

    yDoc.transact(() => {
      const oldFrame = nodes.get(currentParentId) as Y.Map<unknown> | undefined;
      if (oldFrame) {
        const oldOrder = (oldFrame.get('data') as Y.Map<unknown>).get('childOrder') as Y.Array<string>;
        removeFromOrder(oldOrder, args.nodeId);
      }
      node.set('parentId', null);
      node.set('position', args.absoluteEndPos);
    }, origin);
  }, [yDoc, origin, binding]);

  return { commit };
}

function removeFromOrder(order: Y.Array<string>, id: string): void {
  const arr = order.toArray();
  const idx = arr.indexOf(id);
  if (idx !== -1) order.delete(idx, 1);
}
